import { AsYouType, CountryCode } from "libphonenumber-js";
import * as yup from "yup";

/**
 * Helper function to convert Yup validation to Ant Design rules
 * @param schema - Yup schema object
 * @param fieldName - Name of the field to validate
 * @returns Array of Ant Design validation rules
 */
export const getFieldRules = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: yup.ObjectSchema<any>,
  fieldName: string,
) => {
  return [
    {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      validator: async (_: any, value: any) => {
        try {
          await schema.validateAt(fieldName, { [fieldName]: value });
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            throw new Error(error.message);
          }
        }
      },
    },
  ];
};

/**
 * Helper function to filter only numeric characters from input
 * @param value - Input string value
 * @returns String containing only numeric characters
 */
export const filterNumericInput = (value: string): string => {
  return value.replace(/[^0-9]/g, "");
};

/**
 * Helper function to format phone/fax number with dashes (US format: 123-456-7890)
 * @param value - Input string value (should contain only numbers)
 * @returns Formatted number string (e.g., "123-456-7890")
 */
export const formatPhoneFaxNumber = (value: string): string => {
  // Remove all non-numeric characters first
  const numbers = value.replace(/[^0-9]/g, "");

  // Format based on length (US phone/fax numbers are 10 digits)
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    // For 7+ digits, format as: 123-456-7890 (limit to 10 digits to US format)
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

export const formatPhoneWithCountry = (
  phoneNumber?: string,
  countryIso2?: string,
): string => {
  if (!phoneNumber) return "";
  if (!countryIso2) return phoneNumber;
  try {
    return (
      new AsYouType(countryIso2 as CountryCode).input(phoneNumber) ||
      phoneNumber
    );
  } catch {
    return phoneNumber;
  }
};
